import React, { useMemo, useState } from 'react';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { toast } from 'react-hot-toast';
import useGetPartnerProfile from '../../hooks/useGetPartnerProfile';
import Skeleton from '@mui/material/Skeleton';
import { useProfile } from '../../context/ProfileContext';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import Button from '@mui/material/Button';
import { HandshakeOutlinedIcon, PeopleAltOutlinedIcon } from '../../assets/mui';

interface ReferAndEarnProps {}

const copiedNotify = () =>
  toast.success('Copied', {
    duration: 2500,
    position: 'top-center',
  });

const ReferAndEarn: React.FC<ReferAndEarnProps> = () => {
  const { data, isLoading } = useGetPartnerProfile();
  const { profileData } = useProfile();

  const { referralCodes } = data?.data || {};

  const latestReferral = useMemo(() => {
    return referralCodes?.[referralCodes?.length - 1];
  }, [referralCodes]);

  function copyToClipboard(text: string): void {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        copiedNotify();
      })
      .catch((error) => {
        console.error('Error copying text to clipboard:', error);
      });
  }

  const getShareMessage = (section: 'partner' | 'client') => {
    return section === 'partner'
      ? `Hey!
I wanted to share this opportunity with you — you can now become a Tap Partner and earn commissions by referring clients to invest in exclusive products like Invoice Discounting, Asset Leasing, and Pre-IPOs.
Use my referral link to sign up as a partner and start building your earnings:
https://partner.tapinvest.in/login?refCode=${latestReferral?.referralCode}
P.S. You'll earn every time your client invests. Weekly rewards & referral bonuses included!
T&C Apply`
      : `Hey!
I've been helping clients invest smarter through Tap Partner, which offers exclusive opportunities like Invoice Discounting, Asset Leasing, and Pre-IPO investments and more - all designed for better and easy returns.
You can explore and invest directly through Tap.
Activate your account using my referral link:
https://ultra.go.link/ref?adj_t=1iwdprfa&rc=${latestReferral?.referralCode}
P.S. You'll unlock access to curated investment products — and get bonus rewards on your first investment.
T&C Apply`;
  };

  const shareViaWhatsApp = (section: 'partner' | 'client') => {
    const message = getShareMessage(section);
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const shareViaEmail = (section: 'partner' | 'client') => {
    const message = getShareMessage(section);
    window.open(`mailto:?body=${encodeURIComponent(message)}`, '_blank');
  };

  const shareViaSMS = (section: 'partner' | 'client') => {
    const message = getShareMessage(section);
    window.open(`sms:?&body=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className={`flex flex-col gap-4 w-full`}>
      {/* Partner Referral */}
      {profileData?.clientActions?.enablePartnerReferral && (
        <div className={`flex-1 min-h-80 p-5 border bg-white border-zinc-200 rounded-lg`}>
          <div
            className="p-2 rounded-full w-fit mb-2"
            style={{ boxShadow: '0px 2px 6px 0px #0000000F' }}
          >
            <HandshakeOutlinedIcon />
          </div>
          {isLoading ? (
            <Skeleton width={80} height={40} />
          ) : (
            <h3 className="text-lg font-semibold mb-2">Partner Activation</h3>
          )}
          {isLoading ? (
            <Skeleton width="100%" height={40} />
          ) : (
            <p className="text-slate-500 text-sm">Earn up to ₹2500 per activation</p>
          )}
          {!isLoading ? (
            <div className="mt-6 flex flex-col gap-4">
              {/* Partner Invite Link */}
              <div className="w-full">
                <div className="relative flex border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                  <input
                    type="text"
                    readOnly
                    value="partner.tapinvest.in/"
                    name="inviteLink"
                    className="p-2.5 flex-1 focus:outline-none text-slate-400 text-sm font-semibold"
                  />
                  <p className="text-slate-800 absolute left-[148px] top-2.5 bottom-0 text-sm font-semibold">
                    {latestReferral?.referralCode}
                  </p>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `https://partner.tapinvest.in/login?refCode=${latestReferral?.referralCode}`,
                      )
                    }
                    type="button"
                    className="bg-gradient-to-b from-white via-black/0 to-black/[0.04] border-l border-l-gray-300 px-5 text-slate-600"
                  >
                    <FileCopyIcon fontSize="small" />
                  </button>
                </div>
                <div className="mt-4">
                  <p className="text-slate-500 text-sm mb-3">or share via</p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => shareViaEmail('partner')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <EmailIcon className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => shareViaWhatsApp('partner')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <WhatsAppIcon className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => shareViaSMS('partner')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <SmsIcon className="text-slate-600" />
                    </button>
                  </div>
                </div>
                <div className="mt-6">
                  <Button
                    variant="outlined"
                    className="!bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.04)_132.14%),#FFFFFF] border !border-stone-300 flex-1 flex items-center justify-center gap-2 py-2 px-4 !capitalize !rounded-lg !hover:bg-slate-50 transition-colors w-full !text-slate-900"
                    onClick={() => {
                      window.location.href = '/terms?id=partner-referral';
                    }}
                  >
                    Know More
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              <Skeleton width="100%" height={60} />
              <Skeleton width="100%" height={60} />
            </div>
          )}
        </div>
      )}

      {/* Link Only Partner Referral */}
      {profileData?.clientActions?.enableLinkOnlyPartnerReferral && (
        <div className={`flex-1 min-h-80 p-5 border bg-white border-zinc-200 rounded-lg`}>
          <div
            className="p-2 rounded-full w-fit mb-2"
            style={{ boxShadow: '0px 2px 6px 0px #0000000F' }}
          >
            <HandshakeOutlinedIcon />
          </div>
          {isLoading ? (
            <Skeleton width={80} height={40} />
          ) : (
            <h3 className="text-lg font-semibold mb-2">Partner Activation</h3>
          )}
          {isLoading ? (
            <Skeleton width="100%" height={40} />
          ) : (
            <p className="text-slate-500 text-sm">
              Share your code to bring more partners on this platform
            </p>
          )}
          {!isLoading ? (
            <div className="mt-6 flex flex-col gap-4">
              {/* Partner Invite Link */}
              <div className="w-full">
                <div className="relative flex border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                  <input
                    type="text"
                    readOnly
                    value="partner.tapinvest.in/"
                    name="inviteLink"
                    className="p-2.5 flex-1 focus:outline-none text-slate-400 text-sm font-semibold"
                  />
                  <p className="text-slate-800 absolute left-[148px] top-2.5 bottom-0 text-sm font-semibold">
                    {latestReferral?.referralCode}
                  </p>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `https://partner.tapinvest.in/login?refCode=${latestReferral?.referralCode}`,
                      )
                    }
                    type="button"
                    className="bg-gradient-to-b from-white via-black/0 to-black/[0.04] border-l border-l-gray-300 px-5 text-slate-600"
                  >
                    <FileCopyIcon fontSize="small" />
                  </button>
                </div>
                <div className="mt-4">
                  <p className="text-slate-500 text-sm mb-3">or share via</p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => shareViaEmail('partner')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <EmailIcon className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => shareViaWhatsApp('partner')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <WhatsAppIcon className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => shareViaSMS('partner')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <SmsIcon className="text-slate-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              <Skeleton width="100%" height={60} />
              <Skeleton width="100%" height={60} />
            </div>
          )}
        </div>
      )}

      {/* Client Referral */}
      {profileData?.clientActions?.enableClientReferral && (
        <div className={`flex-1 min-h-80 p-5 border bg-white border-zinc-200 rounded-lg`}>
          <div
            className="p-2 rounded-full w-fit mb-2"
            style={{ boxShadow: '0px 2px 6px 0px #0000000F' }}
          >
            <PeopleAltOutlinedIcon />
          </div>
          {isLoading ? (
            <Skeleton width={80} height={40} />
          ) : (
            <h3 className="text-lg font-semibold mb-2">Client Activation</h3>
          )}
          {isLoading ? (
            <Skeleton width="100%" height={40} />
          ) : (
            <p className="text-slate-500 text-sm">Earn up to ₹500 per activation</p>
          )}
          {!isLoading ? (
            <div className="mt-6 flex flex-col gap-4">
              {/* Client Invite Link */}
              <div className="w-full">
                <div className="relative flex border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                  <input
                    type="text"
                    readOnly
                    value="https://ultra.go.link/"
                    name="inviteLink"
                    className="p-2.5 flex-1 focus:outline-none text-slate-400 text-sm font-semibold"
                  />
                  <p className="text-slate-800 absolute left-[146px] top-2.5 bottom-0 text-sm font-semibold">
                    {latestReferral?.referralCode}
                  </p>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `https://ultra.go.link/ref?adj_t=1iwdprfa&rc=${latestReferral?.referralCode}`,
                      )
                    }
                    type="button"
                    className="bg-gradient-to-b from-white via-black/0 to-black/[0.04] border-l border-l-gray-300 px-5 text-slate-600"
                  >
                    <FileCopyIcon fontSize="small" />
                  </button>
                </div>
                <div className="mt-4">
                  <p className="text-slate-500 text-sm mb-3">or share via</p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => shareViaEmail('client')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <EmailIcon className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => shareViaWhatsApp('client')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <WhatsAppIcon className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => shareViaSMS('client')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <SmsIcon className="text-slate-600" />
                    </button>
                  </div>
                </div>
                <div className="mt-6">
                  <Button
                    variant="outlined"
                    className="!bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.04)_132.14%),#FFFFFF] border !border-stone-300 flex-1 flex items-center justify-center gap-2 py-2 px-4 !rounded-lg !hover:bg-slate-50 transition-colors w-full !text-slate-900 !capitalize"
                    onClick={() => {
                      window.location.href = '/terms?id=client-referral';
                    }}
                  >
                    Know More
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              <Skeleton width="100%" height={60} />
              <Skeleton width="100%" height={60} />
            </div>
          )}
        </div>
      )}

      {/* Link Only Client Referral */}
      {profileData?.clientActions?.enableLinkOnlyClientReferral && (
        <div className={`flex-1 min-h-80 p-5 border bg-white border-zinc-200 rounded-lg`}>
          <div
            className="p-2 rounded-full w-fit mb-2"
            style={{ boxShadow: '0px 2px 6px 0px #0000000F' }}
          >
            <PeopleAltOutlinedIcon />
          </div>
          {isLoading ? (
            <Skeleton width={80} height={40} />
          ) : (
            <h3 className="text-lg font-semibold mb-2">Client Activation</h3>
          )}
          {isLoading ? (
            <Skeleton width="100%" height={40} />
          ) : (
            <p className="text-slate-500 text-sm">
              Share your code to bring more clients on this platform
            </p>
          )}
          {!isLoading ? (
            <div className="mt-6 flex flex-col gap-4">
              {/* Client Invite Link */}
              <div className="w-full">
                <div className="relative flex border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                  <input
                    type="text"
                    readOnly
                    value="https://ultra.go.link/"
                    name="inviteLink"
                    className="p-2.5 flex-1 focus:outline-none text-slate-400 text-sm font-semibold"
                  />
                  <p className="text-slate-800 absolute left-[146px] top-2.5 bottom-0 text-sm font-semibold">
                    {latestReferral?.referralCode}
                  </p>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `https://ultra.go.link/ref?adj_t=1iwdprfa&rc=${latestReferral?.referralCode}`,
                      )
                    }
                    type="button"
                    className="bg-gradient-to-b from-white via-black/0 to-black/[0.04] border-l border-l-gray-300 px-5 text-slate-600"
                  >
                    <FileCopyIcon fontSize="small" />
                  </button>
                </div>
                <div className="mt-4">
                  <p className="text-slate-500 text-sm mb-3">or share via</p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => shareViaEmail('client')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <EmailIcon className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => shareViaWhatsApp('client')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <WhatsAppIcon className="text-slate-600" />
                    </button>
                    <button
                      onClick={() => shareViaSMS('client')}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      <SmsIcon className="text-slate-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              <Skeleton width="100%" height={60} />
              <Skeleton width="100%" height={60} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ReferAndEarn.displayName = 'ReferAndEarn';

export default ReferAndEarn;
